/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM, T } from "@adapptio/lib-components";
import { matchAndSort } from "@adapptio/lib-shared";
import React, { useMemo, FC, useState, useCallback, DragEvent, useRef } from "react";
import { TComponentCategoryMap } from "../../common/ComponentCategories";
import { REFERENCE_TYPE } from "../../modules/Core/ReferenceGuide/ReferenceGuideTypes";
import { useCommand } from "../hooks";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { Icon, ICON_SIZE } from "./Icon";
import { SearchBox, SEARCHBOX_SIZE, SEARCHBOX_STYLE } from "./SearchBox";
import { ToolbarButton } from "./Toolbar/ToolbarButton";
import { Tooltip } from "./Tooltip";

export interface IComponentPaletteItem {
	/** Component name */
	name: string;
	/** Component category */
	category: string;
	/** Label (for UI) */
	label: string;
	/** Description (for UI) */
	description: string;
	/** Icon (for palette in UI) */
	icon: string;
	/** Order in editor palette */
	order?: number;
	/** Link to the component documentation */
	docUrl?: string;
	/** If should be hidden from the list */
	hidden?: boolean;
}

interface IComponentPaletteCategory {
	name: string;
	label: string;
	items: IComponentPaletteItem[];
	order: number;
}

enum COMPONENT_PALETTE_STYLE {
	LIST = "list",
	GALLERY = "gallery"
}

export interface IComponentPaletteItemProps {
	item: IComponentPaletteItem;
	style: COMPONENT_PALETTE_STYLE;
	onDragStart: (item: IComponentPaletteItem, ev: DragEvent<HTMLLIElement>) => void;
}

const ComponentPaletteItem: FC<IComponentPaletteItemProps> = ({ item, style, onDragStart }) => {
	const elRef = useRef<HTMLLIElement>();
	const baseClass = BEM("edt-component-palette", "item");

	const handleDragStart = useCallback(
		(ev: DragEvent<HTMLLIElement>) => {
			onDragStart(item, ev);
		},
		[ item, onDragStart ]
	);

	const [ selectReferenceNavItem ] = useCommand("referenceGuide.open");

	const openDoc = useCallback(() => {
		selectReferenceNavItem({
			name: item.name,
			type: REFERENCE_TYPE.COMPONETS
		});
	}, [ item ]);

	return (
		<li className={baseClass.toClassName()} ref={elRef} draggable={true} onDragStart={handleDragStart}>
			<span className={baseClass.element("item-icon").toClassName()}>
				<Icon name={item.icon} size={ICON_SIZE.LARGE} />
			</span>
			{style === COMPONENT_PALETTE_STYLE.LIST ? (
				<span className={baseClass.element("item-info").toClassName()}>
					<span className={baseClass.element("item-label").toClassName()}>
						<T domain="editor" term={item.label} />
					</span>
					<span className={baseClass.element("item-description").toClassName()}>
						<T domain="editor" term={item.description} />
					</span>
				</span>
			) : (
				<>
					<span className={baseClass.element("item-label").toClassName()}>
						<T domain="editor" term={item.label} />
					</span>
					<Tooltip
						for={elRef}
						openDelay={1000}
						className={baseClass.element("item-tooltip").toClassName()}
					>
						<span className={baseClass.element("item-description").toClassName()}>
							<T domain="editor" term={item.description} />
						</span>
					</Tooltip>
				</>
			)}
			<button className={baseClass.element("item-doc-btn").toClassName()} onClick={openDoc}>
				<Icon name="mdi/help-circle" size={ICON_SIZE.XSMALL} />
			</button>
		</li>
	);
};

export interface IComponentPaletteProps {
	components: IComponentPaletteItem[];
	categories: TComponentCategoryMap;
	onDragStart: (item: IComponentPaletteItem, ev: DragEvent<HTMLLIElement>) => void;
}

export const ComponentPalette: FC<IComponentPaletteProps> = ({ components, categories, onDragStart }) => {
	const [ displayStyle, setDisplayStyle ] = useLocalStorage(
		"component-palette-display-style",
		COMPONENT_PALETTE_STYLE.LIST
	);
	const [ searchValue, setSearchValue ] = useState("");

	const toggleDisplayStyle = useCallback(() => {
		setDisplayStyle((prev) =>
			prev === COMPONENT_PALETTE_STYLE.LIST
				? COMPONENT_PALETTE_STYLE.GALLERY
				: COMPONENT_PALETTE_STYLE.LIST
		);
	}, [ setDisplayStyle ]);

	const catagoryList = useMemo(() => {
		const categoryMap: { [K: string]: IComponentPaletteCategory } = {};

		const filteredComponents = components.filter((item) => {
			return item.hidden !== true;
		});

		const matchedComponents =
			searchValue !== ""
				? matchAndSort(filteredComponents, searchValue, {
						keys: [
							{ key: "name", threshold: 2 },
							{ key: "label", threshold: 2 },
							{ key: "description", threshold: 4 }
						],
						baseSort: (a, b) =>
							a.item.order > b.item.order ? 1 : a.item.order < b.item.order ? -1 : 0
				  })
				: filteredComponents.sort((a, b) => (a.order > b.order ? 1 : a.order < b.order ? -1 : 0));

		matchedComponents.forEach((item) => {
			const catName = item.category;

			if (!categoryMap[catName]) {
				categoryMap[catName] = {
					name: catName,
					label: categories[catName]?.label || catName,
					order: categories[catName]?.order || 0,
					items: []
				};
			}

			categoryMap[item.category].items.push(item);
		});

		return Object.keys(categoryMap)
			.map((catName) => {
				return categoryMap[catName];
			})
			.sort((a, b) => (a.order > b.order ? 1 : a.order < b.order ? -1 : 0));
	}, [ components, categories, searchValue ]);

	const handleSearch = (value: string) => {
		setSearchValue(value);
	};

	const baseClass = BEM("edt-component-palette");
	baseClass.mod("style-" + displayStyle);

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("header").toClassName()}>
				<SearchBox
					value={searchValue}
					onChange={handleSearch}
					placeholder="Search..."
					size={SEARCHBOX_SIZE.SMALL}
					style={SEARCHBOX_STYLE.TRANSPARENT_NAKED_ROUND}
				/>
				{displayStyle === COMPONENT_PALETTE_STYLE.GALLERY ? (
					<ToolbarButton
						icon="mdi/view-list"
						onClick={toggleDisplayStyle}
						tooltip="Display as list"
					/>
				) : (
					<ToolbarButton icon="mdi/apps" onClick={toggleDisplayStyle} tooltip="Display as grid" />
				)}
			</div>
			<div className={baseClass.element("container").toClassName()}>
				{catagoryList.map((cat) => {
					return (
						<div key={cat.name} className={baseClass.element("category").toClassName()}>
							<h2 className={baseClass.element("category-name").toClassName()}>
								<T domain="editor" term={cat.label} />
							</h2>
							<ul className={baseClass.element("category-items").toClassName()}>
								{cat.items.map((item) => (
									<ComponentPaletteItem
										key={item.name}
										item={item}
										style={displayStyle}
										onDragStart={onDragStart}
									/>
								))}
							</ul>
						</div>
					);
				})}
			</div>
		</div>
	);
};
